import { Button } from "../../../ui/Button";
import { COLORS, LAYOUT, TEXT_STYLES } from "../../../constants";
import { RunState, newRun, healPlayer } from "../../../state/runState";
import { EncounterScene } from "../../EncounterScene";
import { BaseScreen } from "../../../screens/BaseScreen";
import { tagTextForAutomation } from "../../../automation/automationTags";
import { OVERWORLD_SCENE_KEY } from "../../OverworldScene";
import { OverworldState, newOverworldState } from "../../../state/overworldState";
import { markZoneCompleted, resetOverworldAfterReward } from "../../../state/overworldLogic";
import { navLog } from "../../../debugNavigation";
import type { ScreenContext } from "../../../screens/types";

export class RestScreen extends BaseScreen {
  private run!: RunState;
  private overworldState?: OverworldState;

  constructor(scene: EncounterScene) {
    super(scene, "REST");
  }

  enter(context?: ScreenContext) {
    this.overworldState = context?.overworldState ?? this.scene.getOverworldState?.();
    this.run = context?.run ?? this.overworldState?.run ?? newRun();

    const { width, height } = this.scene.scale;

    // Title
    const title = this.scene.add
      .text(width / 2, LAYOUT.screenPadding + 100, "Campfire Rest", TEXT_STYLES.title)
      .setOrigin(0.5, 0);
    this.addToContainer(title);
    tagTextForAutomation(title, "rest-title");

    // Flavor text
    const flavorText = this.scene.add
      .text(
        width / 2,
        LAYOUT.screenPadding + 180,
        "You rest by the campfire, recovering your strength.",
        { ...TEXT_STYLES.body, align: "center", wordWrap: { width: width - 100 } }
      )
      .setOrigin(0.5, 0);
    this.addToContainer(flavorText);
    tagTextForAutomation(flavorText, "rest-flavor");

    // Heal amount preview
    const currentHp = this.run.player.hp;
    const maxHp = this.run.player.maxHp;
    const healAmount = Math.floor(maxHp * 0.4);
    const finalHp = Math.min(currentHp + healAmount, maxHp);
    const healPreview = this.scene.add
      .text(
        width / 2,
        height / 2 - 40,
        `Heal ${healAmount} HP (40% of max)\n${currentHp} -> ${finalHp} HP`,
        { ...TEXT_STYLES.subtitle, color: COLORS.healText, align: "center" }
      )
      .setOrigin(0.5, 0.5);
    this.addToContainer(healPreview);
    tagTextForAutomation(healPreview, "rest-heal-preview");

    // Rest button
    const buttonWidth = width - LAYOUT.screenPadding * 2;
    const restButton = new Button(this.scene, LAYOUT.screenPadding, height / 2 + 60, {
      width: buttonWidth,
      height: LAYOUT.buttonHeight,
      label: "Rest (no reward)",
      automationName: "rest-button",
      onClick: () => {
        this.onRest();
      },
    });
    this.addToContainer(restButton);
  }

  private onRest() {
    // Heal 40% of max HP
    healPlayer(this.run, 0.4);

    // Update overworld state with current run
    const overworldState = this.overworldState ?? newOverworldState(this.run);
    overworldState.run = this.run;

    // Mark rest zone as completed without advancing encounter count.
    markZoneCompleted(overworldState, overworldState.activeZoneId);
    resetOverworldAfterReward(overworldState);

    navLog("RestScreen", "Resting complete, returning to overworld");

    // Return to overworld
    this.scene.overlay.transition(() => {
      this.scene.scene.start(OVERWORLD_SCENE_KEY, { overworldState });
    });
  }
}
